/**
 * Permission codes the Service screens declare an interest in.
 *
 * These are the dot-strings `internal/service/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6). Naming them once here means a renamed
 * permission is a compile error rather than a silently invisible button.
 *
 * # The foreign codes are not optional extras
 *
 * `internal/service/permissions.go` says it in its own header: a ticket workflow
 * needs every permission the whole path consumes, not only this module's codes.
 * That is because `internal/service` does almost nothing itself — it wraps
 * `crm.Service`, `inventory.Service` and `ar.Service`, and **each of those
 * re-checks its own permission under the same caller**:
 *
 *  - `crm.case.*` — a ticket *is* a case. Creating, assigning, responding,
 *    resolving, closing and reopening all run through CRM, which checks its own
 *    code as well. A user holding `service.ticket.resolve` but not
 *    `crm.case.resolve` gets a 403 from a button that looked available, so the
 *    screens gate on **both**.
 *  - `inventory.stock.issue` — consuming a part is a real stock issue. Voiding
 *    one restocks it, which needs `inventory.stock.receive`.
 *  - `inventory.item.read` / `inventory.warehouse.read` — the part picker.
 *  - `ar.customer.read` — a ticket names the customer it is raised against.
 *  - `ar.invoice.read` — the invoice a billed ticket produced.
 *  - `org.company.read` / `org.account.read` — the company that scopes a ticket,
 *    and the revenue account its invoice credits.
 *  - `admin.user.list` — the technician and assignee pickers.
 *  - `crm.activity.read` / `crm.activity.create` — the ticket's conversation is
 *    the case's activity timeline, rendered by CRM's own component.
 *
 * `canDo` in the screens is therefore usually a conjunction. That is not
 * defensive coding; it is the actual authorization the request will meet.
 */

export const SERVICE_PERMISSIONS = {
  ticketRead: 'service.ticket.read',
  ticketCreate: 'service.ticket.create',
  /** The satellite row only: customer asset, warehouse, billable flag. */
  ticketUpdate: 'service.ticket.update',
  ticketAssign: 'service.ticket.assign',
  ticketRespond: 'service.ticket.respond',
  ticketResolve: 'service.ticket.resolve',
  ticketClose: 'service.ticket.close',
  ticketReopen: 'service.ticket.reopen',
  ticketCsat: 'service.ticket.csat',
  /** Raises, confirms and posts the AR invoice. Once per ticket, ever. */
  ticketBill: 'service.ticket.bill',

  partConsume: 'service.part.consume',
  partVoid: 'service.part.void',

  laborLog: 'service.labor.log',
  laborVoid: 'service.labor.void',

  customerAssetRead: 'service.customer_asset.read',
  customerAssetManage: 'service.customer_asset.manage',

  assignmentRuleManage: 'service.assignment_rule.manage',

  pmScheduleRead: 'service.pmschedule.read',
  pmScheduleManage: 'service.pmschedule.manage',
  pmScheduleRun: 'service.pmschedule.run',

  settingsManage: 'service.settings.manage',

  reportRead: 'service.report.read',

  /* ------------------------------------------------------- C11 additions */

  /**
   * The work order — one document for a customer job and an own-asset job
   * alike (C11 §4.2, "one path, not two").
   *
   * `workOrderReadOwn` is the technician's own row scope: a self-service
   * technician sees the jobs assigned to them and gets **404** — never 403 and
   * never an empty 200 — for anybody else's (§6.15).
   */
  workOrderRead: 'service.workorder.read',
  workOrderReadOwn: 'service.workorder.read.own',
  workOrderCreate: 'service.workorder.create',
  workOrderUpdate: 'service.workorder.update',
  /** The skills and working-time checks fire here, not at assignment. */
  workOrderDispatch: 'service.workorder.dispatch',
  /** The permit check fires here, and stamps the executor. */
  workOrderStart: 'service.workorder.start',
  workOrderComplete: 'service.workorder.complete',
  workOrderClose: 'service.workorder.close',
  workOrderBill: 'service.workorder.bill',

  contractRead: 'service.contract.read',
  contractManage: 'service.contract.manage',
  entitlementRead: 'service.entitlement.read',
  entitlementManage: 'service.entitlement.manage',

  rateCardRead: 'service.ratecard.read',
  rateCardManage: 'service.ratecard.manage',
  /** Publishing FREEZES a version; it is its own permission for that reason. */
  rateCardPublish: 'service.ratecard.publish',

  goodwillRequest: 'service.goodwill.request',
  goodwillApprove: 'service.goodwill.approve',

  vanRead: 'service.van.read',
  vanManage: 'service.van.manage',
  vanReplenish: 'service.van.replenish',

  problemManage: 'service.problem.manage',
  kbReview: 'service.kb.review',

  /* --------------------------------------------- the codes CRM re-checks */

  caseRead: 'crm.case.read',
  caseCreate: 'crm.case.create',
  caseAssign: 'crm.case.assign',
  /** Replying, and recording CSAT — CRM guards both with this one code. */
  caseRespond: 'crm.case.respond',
  caseResolve: 'crm.case.resolve',
  caseClose: 'crm.case.close',
  caseReopen: 'crm.case.reopen',
  /** SLA policies are browsed through CRM's own endpoint, not re-exposed here. */
  caseManage: 'crm.case.manage',
  activityRead: 'crm.activity.read',
  activityCreate: 'crm.activity.create',

  /* ---------------------------------- the codes inventory and AR re-check */

  /** Consuming a part is a stock issue. */
  stockIssue: 'inventory.stock.issue',
  /** Voiding one restocks it. */
  stockReceive: 'inventory.stock.receive',
  itemRead: 'inventory.item.read',
  warehouseRead: 'inventory.warehouse.read',

  customerRead: 'ar.customer.read',
  arInvoiceRead: 'ar.invoice.read',

  /** A permit blocks a work order from starting; the banner reads it. */
  eamPermitRead: 'eam.permit.read',

  companyRead: 'org.company.read',
  accountRead: 'org.account.read',
} as const

export type ServicePermission = (typeof SERVICE_PERMISSIONS)[keyof typeof SERVICE_PERMISSIONS]
